import React, { useContext, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import type { ReactNode } from 'react';
import useIsLargeScreen from '@src/hooks/useIsLargeScreen';
import DisableUserSelect from '@src/components/DisableUserSelect';
import TextHeader from './drawer_headers/Text';
import LayoutHeader from './drawer_headers/Layout';
import {
  TextEditorDispatchContext,
  TextEditorStateContext
} from '@src/contexts/wysiwyg/text_editor';
import {
  WysiwygEditorDispatchContext,
  WysiwygEditorStateContext
} from '@src/contexts/wysiwyg/editor';
import LockBodyScroll from '@src/components/LockBodyScroll';

type TProps = {
  children: ReactNode;
  className?: string;
  header: typeof TextHeader | typeof LayoutHeader;
};

function Drawer(props: TProps): React.ReactElement {
  const { closeToolbox } = useContext(WysiwygEditorDispatchContext);
  const { renderRect } = useContext(WysiwygEditorStateContext);
  const { focusEditor } = useContext(TextEditorDispatchContext);
  const { textEditorContainerFocused } = useContext(TextEditorStateContext);
  const [_rendered, setRendered] = useState(false);
  const drawerRef = useRef(null);
  const Header = props.header;
  const contentRef = useRef<HTMLDivElement>(null);
  const isLarge = useIsLargeScreen();

  useEffect(() => {
    // In order to pass the element as a valid ref to LockBodyScroll, we need to
    // re-render after the elements have rendered. Otherwise the ref is null.
    setRendered(true);
  }, []);

  return (
    <div
      className={classnames(
        'wysiwyg-editor-toolbox-drawer-container',
        props.className
      )}
      style={{ maxHeight: isLarge ? renderRect.height : 'auto' }}
      ref={drawerRef}
    >
      {isLarge ? null : (
        <Header
          onClick={() => {
            closeToolbox();

            // applying to entire content means the text editor hasn't been
            // focused to start with
            if (!textEditorContainerFocused) setTimeout(focusEditor, 100);
          }}
        >
          <DisableUserSelect />
          <LockBodyScroll scrollableElementRef={contentRef} />
        </Header>
      )}
      <div className="wysiwyg-editor-toolbox-drawer" ref={contentRef}>
        {props.children}
      </div>
    </div>
  );
}

export default Drawer;
